import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { submitForm } from 'shared/thunks/submitForm/submitForm';
import { ERoomsCount, IDobroForm, IDobroFormErrors } from 'shared/types/form';
import { locationVisible, priceDescriptionVisible, studioValueVisible } from 'shared/utils/dobroForm/visible';
import { initialErrorsState, validateForm } from '../../utils/dobroForm/validateForm';

export interface IFormState {
  submitted: boolean;
  saving: boolean;
  saved: boolean;
  savingError: string | null;
  data: IDobroForm;
  errors: IDobroFormErrors;
}

const initialState: IFormState = {
  submitted: false,
  saving: false,
  saved: false,
  savingError: null,
  data: {
    objectType: null,
    moscow: null,
    location: '',
    address: '',
    guestsCount: null,
    availableFor: [],
    readyAccommodate: [],
    roomsCount: null,
    bedsCount: null,
    bedsDescription: '',
    facilities: [],
    area: null,
    floor: null,
    priceType: null,
    priceDescription: '',
    availablePeriod: '',
    otherWishes: '',
    phone: '',
    additionalPhone: '',
    name: '',
    telegram: '',
    email: '',
    offerSigned: false,
  },
  errors: initialErrorsState,
};

/* istanbul ignore next */
function cleanupState(state: IFormState) {
  if (!studioValueVisible(state) && state.data.roomsCount === ERoomsCount.Studio) {
    state.data.roomsCount = null;
  }
  if (!locationVisible(state) && state.data.location !== '') {
    state.data.location = '';
  }
  if (!priceDescriptionVisible(state) && state.data.priceDescription !== '') {
    state.data.priceDescription = '';
  }
}

function cb<K extends keyof IDobroForm>(key: K) {
  return (state: IFormState, action: PayloadAction<IDobroForm[K]>) => {
    state.data[key] = action.payload;
    cleanupState(state);
    validateForm(state);
  };
}

const formSlice = createSlice({
  initialState,
  name: 'form',
  reducers: {
    markSubmitted: state => {
      state.submitted = true;
      validateForm(state);
    },
    setObjectType: cb('objectType'),
    setMoscow: cb('moscow'),
    setLocation: cb('location'),
    setAddress: cb('address'),
    setGuestsCount: cb('guestsCount'),
    setRoomsCount: cb('roomsCount'),
    setArea: cb('area'),
    setFloor: cb('floor'),
    setBedsCount: cb('bedsCount'),
    setBedsDescription: cb('bedsDescription'),
    setPriceDescription: cb('priceDescription'),
    setFacilities: cb('facilities'),
    setAvailableFor: cb('availableFor'),
    setReadyAccommodate: cb('readyAccommodate'),
    setPriceType: cb('priceType'),
    setAvailablePeriod: cb('availablePeriod'),
    setOtherWishes: cb('otherWishes'),
    setPhone: cb('phone'),
    setAdditionalPhone: cb('additionalPhone'),
    setName: cb('name'),
    setTelegram: cb('telegram'),
    setEmail: cb('email'),
    setOfferSigned: cb('offerSigned'),
  },
  extraReducers(builder) {
    builder.addCase(submitForm.pending, state => {
      state.saving = true;
      state.savingError = null;
    });
    builder.addCase(submitForm.fulfilled, (state, action) => {
      state.saving = false;
      state.saved = action.payload;
    });
    builder.addCase(submitForm.rejected, (state, action) => {
      state.saving = false;
      state.saved = false;
      state.savingError = action.error.message || /* istanbul ignore next */ 'Unknown error';
      // oxlint-disable-next-line no-console
      console.error(state.savingError);
    });
  },
});

export const {
  reducer: formReducer,
  actions: {
    setObjectType,
    setMoscow,
    setLocation,
    setAddress,
    markSubmitted,
    setGuestsCount,
    setRoomsCount,
    setArea,
    setFloor,
    setBedsCount,
    setBedsDescription,
    setFacilities,
    setAvailablePeriod,
    setOtherWishes,
    setPhone,
    setAdditionalPhone,
    setName,
    setOfferSigned,
    setPriceType,
    setPriceDescription,
    setAvailableFor,
    setReadyAccommodate,
    setTelegram,
    setEmail,
  },
} = formSlice;
